import React, { useMemo } from 'react';
import { TileToolbar } from 'widgets/tiles/components/tile/TileToolbar';
import { SymbolFromConfigOrPortfolioLinkedStatus } from 'portfolio/components/SymbolFromConfigOrPortfolioLinkedStatus';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { WidgetTile } from 'widgets/tiles/components/tile/WidgetTile';
import classNames from 'classnames';
import { SignalTileConfigState } from 'widgets/signalTiles/signalTile/state/config/SignalTileConfigState';
import SignalTileSettings from 'widgets/signalTiles/signalTile/components/settings/SignalTileSettings';
import { SignalTileContent } from 'widgets/signalTiles/signalTile/components/SignalTileContent';
import { SignalTileDataState } from 'widgets/signalTiles/signalTile/state/stream/SignalTileStreamState';
import { AnySeries } from '@d3fc/d3fc-series';
import ConfigSettingsModal from 'widgets/tiles/components/tile/ConfigSettingsModal';
import { StreamMessage } from 'utils/messages/StreamMessage';

export interface SignalTileProps {
    className: string;
    config: SignalTileConfigState;
    dispatchConfigChanged: (
        configDelta: Partial<SignalTileConfigState>
    ) => void;
    stream: SignalTileDataState | undefined;
    messages?: StreamMessage[];
    help: JSX.Element;
    settingsHeader: string;
    title: string;
    upperBoundary: number;
    lowerBoundary: number;
    domain: [number, number];
    series: AnySeries;
}

export function SignalTile(props: SignalTileProps) {
    const {
        className,
        stream,
        messages,
        config,
        dispatchConfigChanged,
        help,
        settingsHeader,
        title,
        upperBoundary,
        lowerBoundary,
        domain,
        series,
    } = props;

    useWidgetStream();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const warning = useMemo(() => <SymbolWarning />, [config]);

    const cssClasses = classNames('SignalTile', className);

    return (
        <WidgetTile className={cssClasses}>
            <TileToolbar
                settingsBody={
                    <SignalTileSettings
                        config={config}
                        dispatchConfigChanged={dispatchConfigChanged}
                    />
                }
                settingsHeader={settingsHeader}
                title={title}
                preceedingItems={help}
                messages={messages}
            />

            <div className="capsule-body">
                <SymbolFromConfigOrPortfolioLinkedStatus warning={warning}>
                    <SignalTileContent
                        streamData={stream}
                        config={config}
                        domain={domain}
                        series={series}
                        upperBoundary={upperBoundary}
                        lowerBoundary={lowerBoundary}
                        className={`${className}Content`}
                    />
                </SymbolFromConfigOrPortfolioLinkedStatus>
            </div>
        </WidgetTile>
    );

    function SymbolWarning() {
        return (
            <span>
                Select a symbol or specify one in the&nbsp;
                <ConfigSettingsModal
                    dispatcher={dispatchConfigChanged}
                    triggerText={'Capsule Settings'}
                    headerText={settingsHeader}
                    renderSettings={(p) => (
                        <SignalTileSettings
                            {...p}
                            config={config}
                            dispatchConfigChanged={dispatchConfigChanged}
                        />
                    )}
                />
                .
            </span>
        );
    }
}
